import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Eye, MousePointer, Ticket, Tag, Clock, ScanLine, ArrowRight,
  Plus, TrendingUp, Facebook, BarChart3, Sparkles, LineChart as LineChartIcon, Trophy, Gift, Send, Copy, MessageCircle, Users, Mail, QrCode, Network, Handshake, Star, Newspaper, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CompletionCard } from "@/components/CompletionCard";
import { FB_MEMBERS_DISPLAY } from "@/lib/brand";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function formatCompact(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace(".0", "")}k`;
  return v.toLocaleString("fr-FR");
}

function KpiCard({ icon: Icon, label, value, hint, color = "text-[#E05638]", tid, threshold = 1, emptyHint }) {
  const v = Number(value || 0);
  // Règle crédibilité : n'affiche le chiffre qu'au-delà d'un seuil (ex. vues ≥ 20)
  const empty = v < threshold;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5" data-testid={tid}>
      <Icon size={18} className={color}/>
      <div className={`mt-2 font-heading text-2xl md:text-3xl font-extrabold ${empty ? "text-slate-300" : "text-slate-900"}`}>
        {empty ? "—" : v}
      </div>
      <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 font-bold mt-1">{label}</div>
      {empty ? (
        <div className="mt-1 text-[11px] text-slate-400">{emptyHint || "Données disponibles dès les premières interactions"}</div>
      ) : (
        hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>
      )}
    </div>
  );
}

export default function ProDashboard() {
  const [s, setS] = useState(null);
  const [scans, setScans] = useState([]);
  const [offers, setOffers] = useState([]);
  const [homeStats, setHomeStats] = useState({ fb_groups: 0, fb_members_total: 0 });
  const [biz, setBiz] = useState(null);
  const [net, setNet] = useState(null);
  const [cit, setCit] = useState(null);
  const [ts, setTs] = useState(null);
  const [period, setPeriod] = useState(30);
  const [ref, setRef] = useState(null);
  const [emails, setEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [sp, setSp] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(sp.get("welcome") === "1");
  const dismissWelcome = () => { setShowWelcome(false); sp.delete("welcome"); setSp(sp, { replace: true }); };

  const loadRef = () => api.get("/pro/referral").then((r) => setRef(r.data)).catch(() => {});
  useEffect(() => { loadRef(); }, []);

  const sendInvites = async () => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length) { toast.error("Indiquez au moins une adresse e-mail."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/pro/referral/invite", { emails: list });
      toast.success(`${data.sent} invitation(s) envoyée(s) ! Merci de faire grandir le réseau local.`);
      setEmails(""); loadRef();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
    finally { setSending(false); }
  };

  useEffect(() => {
    api.get("/pro/stats").then((r) => setS(r.data)).catch(() => setS({}));
    api.get("/pro/pass/scans?limit=5").then((r) => setScans(r.data.items || [])).catch(() => {});
    api.get("/pro/offers?limit=100").then((r) => setOffers(r.data || [])).catch(() => {});
    api.get("/public/home-stats").then((r) => setHomeStats(r.data || homeStats)).catch(() => {});
    api.get("/pro/business").then((r) => setBiz(r.data.business)).catch(() => {});
    api.get("/pro/network/dashboard").then((r) => setNet(r.data)).catch(() => {});
    api.get("/pro/citations").then((r) => setCit(r.data)).catch(() => setCit({ total: 0, blog: [], actu: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get(`/pro/stats/timeseries?days=${period}`).then((r) => setTs(r.data)).catch(() => setTs(null));
  }, [period]);

  if (!s) return <div className="text-slate-500">Chargement…</div>;

  // Top offre par vues
  const top = [...offers].sort((a, b) => (b.views || 0) - (a.views || 0))[0];
  const conversionRate = s.views ? Math.round(((s.clicks || 0) / s.views) * 100) : 0;
  const validScans = scans.filter((x) => x.result === "valid").length;

  return (
    <div className="space-y-8" data-testid="pro-dashboard">
      {showWelcome && (
        <div className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-orange-50 p-5 md:p-6" data-testid="pro-welcome-banner">
          <button onClick={dismissWelcome} aria-label="Fermer" data-testid="pro-welcome-dismiss"
                  className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 shrink-0 rounded-xl bg-emerald-600 text-white grid place-items-center"><Sparkles size={22} /></div>
            <div className="min-w-0">
              <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900">Votre espace est prêt 🎉</h2>
              <p className="mt-1 text-sm text-slate-600">
                Bienvenue sur PROXIreduc-28 ! Complétez votre mini-site et publiez vos <strong>offres</strong> pour attirer de nouveaux clients près de chez vous.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/pro/entreprise"><Button className="btn-shine text-white h-10 px-4" data-testid="pro-welcome-fiche"><ArrowRight size={14} className="mr-1.5" />Compléter mon mini-site</Button></Link>
                <Link to="/pro/offres/nouvelle"><Button variant="outline" className="h-10 px-4" data-testid="pro-welcome-offer"><Gift size={14} className="mr-1.5" />Créer ma première offre</Button></Link>
              </div>
            </div>
          </div>
        </div>
      )}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">Vue d'ensemble de votre activité PROXIreduc-28.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/pro/offres/nouvelle">
            <Button className="btn-shine text-white" data-testid="pro-dash-new-offer"><Plus size={16} className="mr-1"/> Nouvelle offre</Button>
          </Link>
          <Link to="/pro/scanner">
            <Button variant="outline" data-testid="pro-dash-scanner"><ScanLine size={16} className="mr-1"/> Scanner un pass</Button>
          </Link>
        </div>
      </header>

      {biz?.completion && !biz.completion.complete && <CompletionCard pct={biz.completion.pct} missing={biz.completion.missing} editTo="/pro/entreprise" />}
      {biz?.completion?.complete && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="pro-minisite-complete-badge">
          <Trophy size={22} className="text-emerald-600 shrink-0" />
          <div>
            <div className="font-heading font-bold text-emerald-800">Mini-site complet 🎉</div>
            <div className="text-sm text-emerald-700">Votre fiche est à 100 %. Elle inspire confiance et gagne en visibilité sur PROXIreduc et Google.</div>
          </div>
        </div>
      )}

      {/* Réseau Pro local — incite à revenir dans l'annuaire */}
      {net && net.has_business && (net.new_pros > 0 || net.pending > 0 || net.recos_week > 0 || net.connections > 0) && (
        <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-orange-50 p-5" data-testid="pro-network-widget">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-sky-600 text-white grid place-items-center shrink-0"><Network size={22} /></div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-sky-600 text-white text-[10px] font-bold uppercase tracking-widest">
                <Sparkles size={11} /> Réseau Pro local
              </div>
              <h3 className="mt-2 font-heading font-extrabold text-slate-900 text-lg" data-testid="pro-network-widget-title">
                {net.new_pros > 0
                  ? `${net.new_pros} nouveau${net.new_pros > 1 ? "x" : ""} pro${net.new_pros > 1 ? "s" : ""} de votre secteur cette semaine`
                  : net.pending > 0
                    ? `${net.pending} demande${net.pending > 1 ? "s" : ""} de connexion en attente`
                    : "Votre réseau pro s'agrandit"}
              </h3>
              <p className="text-sm text-slate-600 mt-0.5">
                {net.sample && net.sample.length > 0
                  ? `Ex. ${net.sample.map((x) => `${x.name}${x.city ? ` (${x.city})` : ""}`).join(", ")}. Connectez-vous pour collaborer.`
                  : "Trouvez des partenaires, échangez des recommandations et créez des collaborations entre pros du 28."}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {net.pending > 0 && <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="pro-network-widget-pending">🤝 {net.pending} demande(s) en attente</span>}
                {net.recos_week > 0 && <span className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-700 font-semibold" data-testid="pro-network-widget-recos"><Star size={11} className="inline -mt-0.5 mr-0.5 fill-amber-500 text-amber-500" />{net.recos_week} reco(s) cette semaine</span>}
                {net.connections > 0 && <span className="px-2 py-1 rounded-full bg-emerald-600 text-white font-semibold" data-testid="pro-network-widget-connections">{net.connections} partenaire(s)</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/pro/reseau"><Button className="btn-shine text-white h-10 px-4" data-testid="pro-network-widget-open"><Network size={15} className="mr-1.5" />Ouvrir mon réseau</Button></Link>
                {net.pending > 0 && <Link to="/pro/reseau/connexions"><Button variant="outline" className="h-10 px-4" data-testid="pro-network-widget-requests"><Handshake size={15} className="mr-1.5" />Voir les demandes</Button></Link>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parrainage — invitez d'autres pros, gagnez 15 jours par filleul */}
      <div className="rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-5" data-testid="pro-referral">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0"><Gift size={22}/></div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-extrabold text-slate-900 text-lg">Parrainez d'autres commerçants — gagnez du temps gratuit</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Invitez des professionnels de votre entourage. Chaque commerce qui rejoint PROXIreduc-28 renforce la visibilité de tout le réseau local. Nous envoyons l'e-mail d'invitation prêt à l'emploi de votre part.
            </p>
            {ref && (ref.registered_count > 0 || ref.invited_count > 0) && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800 font-semibold" data-testid="referral-invited">{ref.invited_count} invitation(s) envoyée(s)</span>
                <span className="px-2 py-1 rounded-full bg-emerald-600 text-white font-semibold" data-testid="referral-registered">{ref.registered_count} filleul(s) inscrit(s) · +{ref.bonus_days} jours gagnés</span>
              </div>
            )}
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={1}
                placeholder="E-mails des pros à inviter (séparés par une virgule)"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="referral-emails-input"/>
              <Button onClick={sendInvites} disabled={sending} className="btn-shine text-white shrink-0" data-testid="referral-send-btn">
                <Send size={15} className="mr-1.5"/> {sending ? "Envoi…" : "Envoyer les invitations"}
              </Button>
            </div>
            {ref?.link && (
              <div className="mt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="referral-link">{ref.link}</code>
                <Button variant="outline" size="sm" className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="referral-copy-btn"
                  onClick={async () => { try { await navigator.clipboard.writeText(ref.link); toast.success("Lien de parrainage copié — partagez-le par WhatsApp, SMS ou dans vos groupes !"); } catch { toast.message("Copie manuelle : sélectionnez le lien ci-contre."); } }}>
                  <Copy size={14} className="mr-1.5"/> Copier mon lien
                </Button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Salut 👋 Je suis partenaire sur PROXIreduc-28, la plateforme de bons plans locale en Eure-et-Loir. Ça m'apporte de la visibilité auprès des clients du coin. Tu peux t'inscrire gratuitement comme commerçant via mon lien, c'est rapide : ${ref.link}`)}`}
                  target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid="referral-whatsapp-btn"
                  onClick={() => toast.success("WhatsApp s'ouvre avec votre message prêt à envoyer.")}>
                  <Button type="button" size="sm" className="w-full bg-[#25D366] hover:bg-[#1ebe5b] text-white">
                    <MessageCircle size={14} className="mr-1.5"/> Partager sur WhatsApp
                  </Button>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Espace Clients (CRM) — fidélisez vos clients */}
      <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-orange-50 p-5" data-testid="pro-crm-promo">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white grid place-items-center shrink-0"><Users size={22}/></div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest">
              <Sparkles size={11}/> Votre Espace Clients
            </div>
            <h3 className="mt-2 font-heading font-extrabold text-slate-900 text-lg">Fidélisez vos clients et faites-les revenir</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Rassemblez vos contacts, envoyez des <b>campagnes e-mail</b> (rédigées et illustrées par l'IA), collectez des inscrits avec un <b>QR « liste VIP »</b> et activez les <b>anniversaires & relances automatiques</b>.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/pro/clients"><Button className="btn-shine text-white h-10 px-4" data-testid="pro-crm-promo-contacts"><Users size={15} className="mr-1.5"/> Mes contacts</Button></Link>
              <Link to="/pro/clients/campagnes"><Button variant="outline" className="h-10 px-4" data-testid="pro-crm-promo-campaigns"><Mail size={15} className="mr-1.5"/> Créer une campagne</Button></Link>
              <Link to="/pro/clients/collecte"><Button variant="outline" className="h-10 px-4" data-testid="pro-crm-promo-collect"><QrCode size={15} className="mr-1.5"/> Mon QR liste VIP</Button></Link>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <KpiCard tid="pro-kpi-views"     icon={Eye}          label="Vues sur mes offres"    value={s.views ?? 0}       color="text-blue-600"
                 threshold={20} emptyHint="Vos premières vues arrivent 🚀"/>
        <KpiCard tid="pro-kpi-clicks"    icon={MousePointer} label="Clics visiteurs"         value={s.clicks ?? 0}      color="text-indigo-600"
                 hint={conversionRate > 0 ? `${conversionRate}% des vues` : null}/>
        <KpiCard tid="pro-kpi-redemptions" icon={Ticket}     label="Utilisations"           value={s.redemptions ?? 0} color="text-emerald-600"/>
        <KpiCard tid="pro-kpi-scans"     icon={ScanLine}     label="Scans pass valides"     value={validScans}         color="text-[#E05638]"
                 hint={scans.length ? `${scans.length} scans au total` : "Aucun scan"}/>
        <KpiCard tid="pro-kpi-active"    icon={Tag}          label="Offres actives"          value={s.active_offers ?? 0}   color="text-slate-800"/>
        <KpiCard tid="pro-kpi-pending"   icon={Clock}        label="En attente"              value={s.pending_offers ?? 0}  color="text-amber-600"/>
      </div>

      {/* Courbe d'évolution — vues / clics / utilisations */}
      {(() => {
        const series = (ts?.series || []).map((d) => ({
          ...d,
          label: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`,
        }));
        const tot = ts?.totals || { views: 0, clicks: 0, redemptions: 0 };
        const hasData = (tot.views + tot.clicks + tot.redemptions) > 0;
        const periods = [[7, "7 j"], [30, "30 j"], [90, "90 j"]];
        return (
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-dash-chart">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
                <LineChartIcon size={18} className="text-[#E05638]" /> Évolution de votre activité
              </div>
              <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid="pro-chart-period">
                {periods.map(([v, lbl]) => (
                  <button key={v} onClick={() => setPeriod(v)} data-testid={`pro-chart-period-${v}`}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${period === v ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {!ts ? (
              <div className="mt-4 h-72 grid place-items-center text-sm text-slate-400">Chargement…</div>
            ) : !hasData ? (
              <div className="mt-4 h-72 grid place-items-center text-center" data-testid="pro-chart-empty">
                <div>
                  <BarChart3 size={36} className="mx-auto text-slate-300" />
                  <div className="mt-2 text-sm text-slate-600 font-semibold">Pas encore d'activité mesurée</div>
                  <div className="mt-1 text-xs text-slate-400 max-w-xs mx-auto">
                    Vos courbes de vues, clics et utilisations apparaîtront ici dès les premières interactions avec vos offres.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-4 h-72">
                  <ResponsiveContainer>
                    <LineChart data={series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                      <XAxis dataKey="label" fontSize={10} tickLine={false} axisLine={{ stroke: "#eee" }}
                             interval="preserveStartEnd" minTickGap={24} />
                      <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #eee" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="views" name="Vues" stroke="#2563eb" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="clicks" name="Clics" stroke="#4f46e5" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="redemptions" name="Utilisations" stroke="#059669" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-blue-50 py-2"><div className="font-heading font-extrabold text-blue-700">{tot.views >= 20 ? tot.views : "—"}</div><div className="text-[10px] uppercase tracking-widest font-bold text-blue-600/70">Vues</div></div>
                  <div className="rounded-xl bg-indigo-50 py-2"><div className="font-heading font-extrabold text-indigo-700">{tot.clicks}</div><div className="text-[10px] uppercase tracking-widest font-bold text-indigo-600/70">Clics</div></div>
                  <div className="rounded-xl bg-emerald-50 py-2"><div className="font-heading font-extrabold text-emerald-700">{tot.redemptions}</div><div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600/70">Utilisations</div></div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Citations dans les articles (blog + revue de presse) — visibilité éditoriale gratuite */}
      {cit && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-citations">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
              <Newspaper size={18} className="text-[#E05638]" /> Vos citations dans les articles
            </div>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-orange-50 text-[#E05638]" data-testid="pro-citations-total">
              Mis en avant {cit.highlight_count ?? cit.total} fois
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Articles du blog local et de la revue de presse qui vous citent{(cit.editorial_placements ?? 0) > 0 ? `, et ${cit.editorial_placements} apparition${cit.editorial_placements > 1 ? "s" : ""} en encart partenaire sur la revue de presse` : ""} — un coup de projecteur gratuit, bon pour votre référencement.
          </p>
          {(cit.blog_placements ?? 0) > 0 && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 text-xs font-semibold" data-testid="pro-blog-placements">
              <Newspaper size={13} /> {cit.blog_placements} apparition{cit.blog_placements > 1 ? "s" : ""} en encart sur nos articles de blog
            </div>
          )}
          {cit.total === 0 ? (
            <div className="mt-4 p-6 rounded-xl border-2 border-dashed border-slate-200 text-center" data-testid="pro-citations-empty">
              <Newspaper size={32} className="mx-auto text-slate-300" />
              <div className="mt-2 text-sm text-slate-600">Vous n'êtes pas encore cité dans un article.</div>
              <div className="mt-1 text-xs text-slate-400 max-w-md mx-auto">
                Nos articles locaux citent en priorité les commerces partenaires actifs. Complétez votre fiche pour gagner en visibilité.
              </div>
            </div>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="pro-citations-list">
              {[...(cit.blog || []).map((a) => ({ ...a, kind: "blog" })), ...(cit.actu || []).map((a) => ({ ...a, kind: "actu" }))].map((a) => (
                <li key={`${a.kind}-${a.slug}`}>
                  <a href={`/${a.kind === "blog" ? "blog" : "actu"}/${a.slug}`} target="_blank" rel="noreferrer"
                     className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`pro-citation-${a.slug}`}>
                    <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                      {a.cover_image
                        ? <img src={a.cover_image} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full grid place-items-center text-slate-300"><Newspaper size={18} /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${a.kind === "blog" ? "bg-indigo-100 text-indigo-700" : "bg-orange-100 text-orange-700"}`}>
                          {a.kind === "blog" ? "Blog" : "Actu"}
                        </span>
                        {a.city && <span className="text-[11px] text-slate-400 truncate">{a.city}</span>}
                      </div>
                      <div className="font-semibold text-slate-900 text-sm truncate">{a.title}</div>
                    </div>
                    <ExternalLink size={14} className="text-slate-400 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Classement des offres — aide le pro à optimiser */}
      {offers.length > 0 && (() => {
        const ranked = [...offers].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
        const maxViews = Math.max(1, ...ranked.map((o) => o.views || 0));
        return (
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-dash-ranking">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
                <Trophy size={18} className="text-amber-500" /> Classement de vos offres
              </div>
              <Link to="/pro/offres" className="text-xs text-[#E05638] font-semibold hover:underline inline-flex items-center gap-1">
                Gérer <ArrowRight size={12} />
              </Link>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Triées par nombre de vues — concentrez vos efforts sur celles qui performent le mieux.
            </p>
            <ul className="mt-4 space-y-3" data-testid="pro-ranking-list">
              {ranked.map((o, i) => (
                <li key={o.id} className="flex items-center gap-3" data-testid={`pro-ranking-row-${i}`}>
                  <span className={`shrink-0 w-7 h-7 rounded-lg grid place-items-center font-heading font-extrabold text-sm ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-slate-700" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-900 truncate text-sm">{o.title}</div>
                      <div className="shrink-0 text-[11px] text-slate-500">{o.views || 0} vues · {o.clicks || 0} clics · {o.redemptions || 0} util.</div>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#E05638] rounded-full transition-all" style={{ width: `${Math.round(((o.views || 0) / maxViews) * 100)}%` }} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* 2 colonnes : Top offre + Scans récents */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-dash-top-offer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
              <TrendingUp size={18} className="text-emerald-600"/> Offre la plus vue
            </div>
            <Link to="/pro/offres" className="text-xs text-[#E05638] font-semibold hover:underline inline-flex items-center gap-1">
              Toutes mes offres <ArrowRight size={12}/>
            </Link>
          </div>
          {top ? (
            <div className="mt-3 flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-orange-50 grid place-items-center text-[#E05638] font-heading font-extrabold text-xl shrink-0">
                {(top.discount || "").replace(/[^0-9%€]/g, "").slice(0, 5) || "—"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-extrabold text-slate-900 truncate">{top.title}</div>
                <div className="text-sm text-slate-500">
                  {top.views || 0} vues · {top.clicks || 0} clics · {top.redemptions || 0} utilisations
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Statut : {top.status}</div>
              </div>
              <Link to={`/pro/offres/${top.id}/editer`}><Button size="sm" variant="outline">Éditer</Button></Link>
            </div>
          ) : (
            <div className="mt-3 p-6 rounded-xl border-2 border-dashed border-slate-200 text-center">
              <Tag size={32} className="mx-auto text-slate-300"/>
              <div className="mt-2 text-sm text-slate-600">Vous n'avez pas encore d'offre publiée.</div>
              <Link to="/pro/offres/nouvelle" className="mt-2 inline-block text-[#E05638] font-semibold text-sm hover:underline">
                Créer ma première offre →
              </Link>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-dash-recent-scans">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
              <ScanLine size={18} className="text-[#E05638]"/> Scans récents
            </div>
            <Link to="/pro/scanner" className="text-xs text-[#E05638] font-semibold hover:underline inline-flex items-center gap-1">
              Ouvrir <ArrowRight size={12}/>
            </Link>
          </div>
          {scans.length === 0 ? (
            <div className="mt-3 p-4 rounded-xl border-2 border-dashed border-slate-200 text-center">
              <ScanLine size={28} className="mx-auto text-slate-300"/>
              <div className="mt-2 text-xs text-slate-500">Aucun scan pour le moment.</div>
              <Link to="/pro/scanner" className="mt-1 inline-block text-[#E05638] font-semibold text-xs hover:underline">
                Scanner un pass
              </Link>
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100 text-sm">
              {scans.slice(0, 5).map((x) => (
                <li key={x.id} className="py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{x.member_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{x.organization_name} · {new Date(x.scanned_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${x.result === "valid" ? "bg-emerald-100 text-emerald-800" : x.result === "inactive" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
                    {x.result}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="pro-dash-quick-actions">
        <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-widest">
          <Sparkles size={12}/> Boostez votre visibilité
        </div>
        <h3 className="mt-2 font-heading text-xl md:text-2xl font-extrabold">3 actions rapides pour maximiser vos résultats</h3>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <Link to="/pro/publications-facebook" className="rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/10 transition-colors">
            <Facebook size={18} className="text-blue-300"/>
            <div className="mt-2 font-heading font-extrabold text-sm">Publier sur les groupes FB</div>
            <div className="mt-1 text-xs text-white/70">
              Diffusion possible auprès de {homeStats.fb_members_display || FB_MEMBERS_DISPLAY} membres en cumulé
            </div>
          </Link>
          <Link to="/pro/scanner" className="rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/10 transition-colors">
            <ScanLine size={18} className="text-[#E05638]"/>
            <div className="mt-2 font-heading font-extrabold text-sm">Scanner les pass en boutique</div>
            <div className="mt-1 text-xs text-white/70">Vérification instantanée</div>
          </Link>
          <Link to="/pro/entreprise" className="rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/10 transition-colors">
            <BarChart3 size={18} className="text-emerald-300"/>
            <div className="mt-2 font-heading font-extrabold text-sm">Compléter ma fiche</div>
            <div className="mt-1 text-xs text-white/70">Meilleur ranking = plus de vues</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
